import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { FindAssetCustomFieldResponseDto } from '@evimo/types/api/asset-custom-field/find-asset-custom-field/find-asset-custom-field.response.dto';
import type { FindAssetCustomFieldValueResponseDto } from '@evimo/types/api/asset-custom-field-value/find-asset-custom-field-value/find-asset-custom-field-value.response.dto';
import { AssetCustomFieldService } from '~/app/services/asset-custom-field/asset-custom-field.service';
import { AssetCustomFieldValueService } from '~/app/services/asset-custom-field-value/asset-custom-field-value.service';
import {
  buildInitialCustomFieldValues,
  buildValueIdMap,
  toStorageValue,
  type CustomFieldFormValues,
} from '~/app/components/asset-custom-fields/utils';

const EMPTY_FIELDS: FindAssetCustomFieldResponseDto[] = [];
const EMPTY_VALUES: FindAssetCustomFieldValueResponseDto[] = [];

export function useAssetCustomFields(options: {
  organizationId?: string | null;
  assetTypeId?: string | null;
  assetId?: string | null;
  enabled?: boolean;
}) {
  const { organizationId, assetTypeId, assetId, enabled = true } = options;
  const queryClient = useQueryClient();

  const definitionsQuery = useQuery({
    queryKey: ['asset-custom-fields', 'by-type', assetTypeId],
    queryFn: () => AssetCustomFieldService.findByAssetType(assetTypeId!),
    enabled: enabled && !!assetTypeId,
  });

  const valuesQuery = useQuery({
    queryKey: [
      'asset-custom-field-values',
      'by-asset',
      organizationId,
      assetId,
    ],
    queryFn: () => {
      if (!organizationId || !assetId) return EMPTY_VALUES;
      return AssetCustomFieldValueService.findByAsset(organizationId, assetId);
    },
    enabled: enabled && !!organizationId && !!assetId,
  });

  const fields = definitionsQuery.data ?? EMPTY_FIELDS;
  const values = valuesQuery.data ?? EMPTY_VALUES;

  const initialValues = useMemo(
    () => buildInitialCustomFieldValues(fields, values),
    [fields, values],
  );

  const valueIdByFieldId = useMemo(() => buildValueIdMap(values), [values]);

  const saveMutation = useMutation({
    mutationFn: async (payload: {
      organizationId: string;
      assetId: string;
      formValues: CustomFieldFormValues;
    }) => {
      const ops = fields.map(async (field) => {
        const nextValue = toStorageValue(
          field,
          payload.formValues[field.id] ?? null,
        );
        const existingId = valueIdByFieldId[field.id];

        if (existingId) {
          await AssetCustomFieldValueService.update(
            payload.organizationId,
            payload.assetId,
            existingId,
            { value: nextValue },
          );
          return;
        }

        if (nextValue === null && !field.required) {
          return;
        }

        await AssetCustomFieldValueService.create(
          payload.organizationId,
          payload.assetId,
          {
            assetCustomFieldId: field.id,
            value: nextValue,
          },
        );
      });

      await Promise.all(ops);
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [
          'asset-custom-field-values',
          'by-asset',
          variables.organizationId,
          variables.assetId,
        ],
      });
    },
  });

  return {
    fields,
    initialValues,
    isLoading:
      definitionsQuery.isLoading || (!!assetId && valuesQuery.isLoading),
    isError: definitionsQuery.isError || valuesQuery.isError,
    saveCustomFieldValues: saveMutation.mutateAsync,
    isSaving: saveMutation.isPending,
  };
}
